<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="store.showModal"
        class="confirmation-overlay"
        @click.self="store.cancel"
      >
        <div class="confirmation-modal" role="dialog" aria-labelledby="confirmation-title">
          <!-- Header -->
          <div class="modal-header" :class="operationClass">
            <div class="header-icon" :class="operationClass">
              <component :is="headerIcon" :size="24" />
            </div>
            <div class="header-text">
              <h2 id="confirmation-title" class="modal-title">{{ modalTitle }}</h2>
              <p class="modal-subtitle">{{ modalSubtitle }}</p>
            </div>
            <!-- Countdown Timer -->
            <div class="countdown-timer" :class="{ urgent: remainingSeconds <= 15 }">
              <Clock :size="16" />
              <span class="countdown-value">{{ formatTime(remainingSeconds) }}</span>
            </div>
            <button class="close-btn" @click="store.cancel" title="Kapat">
              <X :size="20" />
            </button>
          </div>

          <!-- Content: Submit Order -->
          <div v-if="operationType === 'submit'" class="modal-content">
            <div class="order-card">
              <div class="order-header">
                <div class="stock-info">
                  <span class="stock-symbol">{{ orderDetails?.stock || '-' }}</span>
                  <span v-if="orderDetails?.stockName" class="stock-name">{{ orderDetails.stockName }}</span>
                </div>
                <div class="order-direction-badge" :class="orderDirection">
                  {{ isBuyOrder ? 'ALIM' : 'SATIM' }}
                </div>
              </div>

              <div class="order-details-grid editable-grid">
                <div class="detail-item">
                  <label class="detail-label">
                    <Package :size="14" />
                    Miktar
                  </label>
                  <div class="editable-field">
                    <input 
                      type="number" 
                      v-model.number="editableAmount" 
                      class="edit-input"
                      min="1"
                      step="1"
                    />
                    <small class="field-unit">lot</small>
                  </div>
                </div>
                <div class="detail-item">
                  <label class="detail-label">
                    <DollarSign :size="14" />
                    Fiyat
                  </label>
                  <div class="editable-field">
                    <input 
                      type="number" 
                      v-model.number="editablePrice" 
                      class="edit-input"
                      min="0"
                      step="0.01"
                    />
                    <small class="field-unit">₺</small>
                  </div>
                </div>
                <div class="detail-item">
                  <span class="detail-label">
                    <FileText :size="14" />
                    Emir Tipi
                  </span>
                  <span class="detail-value">{{ orderDetails?.orderType || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">
                    <Clock :size="14" />
                    Geçerlilik
                  </span>
                  <span class="detail-value">{{ orderDetails?.validity || '-' }}</span>
                </div>
              </div>

              <div class="estimated-total">
                <span class="total-label">Tahmini Tutar</span>
                <span class="total-value">{{ formatCurrency(calculatedTotal) }}</span>
              </div>
            </div>

            <div class="warning-box">
              <AlertTriangle :size="16" />
              <span>Bu işlem onaylandıktan sonra borsaya iletilecektir.</span>
            </div>
          </div>

          <!-- Content: Modify Order -->
          <div v-else-if="operationType === 'modify'" class="modal-content">
            <div class="order-card modify-card">
              <div class="order-header modify-header">
                <div class="ref-info">
                  <span class="ref-label">Referans No</span>
                  <span class="ref-value">{{ orderDetails?.refNo || '-' }}</span>
                </div>
                <div class="modify-type-badge">
                  {{ orderDetails?.modifyType || 'Değişiklik' }}
                </div>
              </div>

              <div class="modify-changes">
                <!-- Price Change -->
                <div v-if="orderDetails?.oldPrice != null || orderDetails?.newPrice != null" class="change-row">
                  <div class="change-item old">
                    <span class="change-label">Eski Fiyat</span>
                    <span class="change-value">{{ formatCurrency(orderDetails?.oldPrice) }}</span>
                  </div>
                  <div class="change-arrow">
                    <ArrowRight :size="20" />
                  </div>
                  <div class="change-item new editable">
                    <label class="change-label">Yeni Fiyat</label>
                    <div class="editable-field compact">
                      <input 
                        type="number" 
                        v-model.number="editableNewPrice" 
                        class="edit-input"
                        min="0"
                        step="0.01"
                      />
                      <small class="field-unit">₺</small>
                    </div>
                  </div>
                </div>

                <!-- Amount Change -->
                <div v-if="orderDetails?.oldAmount != null || orderDetails?.newAmount != null" class="change-row">
                  <div class="change-item old">
                    <span class="change-label">Eski Miktar</span>
                    <span class="change-value">{{ formatNumber(orderDetails?.oldAmount) }} <small>lot</small></span>
                  </div>
                  <div class="change-arrow">
                    <ArrowRight :size="20" />
                  </div>
                  <div class="change-item new editable">
                    <label class="change-label">Yeni Miktar</label>
                    <div class="editable-field compact">
                      <input 
                        type="number" 
                        v-model.number="editableNewAmount" 
                        class="edit-input"
                        min="1"
                        step="1"
                      />
                      <small class="field-unit">lot</small>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="orderDetails?.orderType || orderDetails?.validity" class="order-meta">
                <span v-if="orderDetails?.orderType">{{ orderDetails.orderType }}</span>
                <span v-if="orderDetails?.orderType && orderDetails?.validity" class="meta-divider">•</span>
                <span v-if="orderDetails?.validity">{{ orderDetails.validity }}</span>
              </div>
            </div>

            <div class="warning-box warning-modify">
              <AlertTriangle :size="16" />
              <span>Mevcut emir değiştirilecektir. Bu işlem geri alınamaz.</span>
            </div>
          </div>

          <!-- Content: Delete Order -->
          <div v-else-if="operationType === 'delete'" class="modal-content">
            <div class="order-card delete-card">
              <div class="delete-icon-wrapper">
                <Trash2 :size="48" />
              </div>
              <div class="delete-info">
                <span class="delete-label">İptal Edilecek Emir</span>
                <span class="delete-ref">Ref No: {{ orderDetails?.refNo || '-' }}</span>
              </div>
            </div>

            <div class="warning-box warning-delete">
              <AlertTriangle :size="16" />
              <span>Bu emir kalıcı olarak iptal edilecektir. Bu işlem geri alınamaz.</span>
            </div>
          </div>

          <!-- Footer Actions -->
          <div class="modal-footer">
            <button 
              class="action-btn decline-btn" 
              @click="store.decline"
              :disabled="store.isResponding"
            >
              <XCircle :size="18" />
              <span>Reddet</span>
            </button>
            <button 
              class="action-btn confirm-btn" 
              :class="operationClass"
              @click="handleConfirm"
              :disabled="store.isResponding"
            >
              <template v-if="store.isResponding">
                <Loader2 :size="18" class="spin" />
                <span>İşleniyor...</span>
              </template>
              <template v-else>
                <component :is="confirmIcon" :size="18" />
                <span>{{ confirmButtonText }}</span>
              </template>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue'
import { 
  X, 
  ArrowUpRight, 
  ArrowDownRight,
  ArrowRight,
  Package, 
  DollarSign, 
  FileText, 
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Edit3,
  Trash2
} from 'lucide-vue-next'
import { useElicitationStore } from '@/stores/elicitation'

const TIMEOUT_SECONDS = 85 // Should match backend ELICITATION_TIMEOUT_MS

const store = useElicitationStore()

const orderDetails = computed(() => store.orderDetails)
const operationType = computed(() => orderDetails.value?.operationType || 'submit')

// Editable fields
const editableAmount = ref<number>(0)
const editablePrice = ref<number>(0)
const editableNewPrice = ref<number>(0)
const editableNewAmount = ref<number>(0)

// Countdown timer
const remainingSeconds = ref(TIMEOUT_SECONDS)
let countdownInterval: ReturnType<typeof setInterval> | null = null

// Watch for modal open to initialize values and start timer
watch(() => store.showModal, (isOpen) => {
  if (isOpen) {
    // Initialize editable values from order details
    editableAmount.value = orderDetails.value?.amount || 0
    editablePrice.value = orderDetails.value?.price || 0
    editableNewPrice.value = orderDetails.value?.newPrice || 0
    editableNewAmount.value = orderDetails.value?.newAmount || 0
    
    // Start countdown
    remainingSeconds.value = TIMEOUT_SECONDS
    startCountdown()
  } else {
    stopCountdown()
  }
})

function startCountdown() {
  stopCountdown()
  countdownInterval = setInterval(() => {
    remainingSeconds.value--
    if (remainingSeconds.value <= 0) {
      stopCountdown()
      store.cancel() // Auto-cancel on timeout
    }
  }, 1000)
}

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

onUnmounted(() => {
  stopCountdown()
})

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Handle confirm with edited values
function handleConfirm() {
  // Build content with edited values
  const content: Record<string, unknown> = {}
  
  if (operationType.value === 'submit') {
    if (editableAmount.value !== orderDetails.value?.amount) {
      content.amount = editableAmount.value
    }
    if (editablePrice.value !== orderDetails.value?.price) {
      content.price = editablePrice.value
    }
  } else if (operationType.value === 'modify') {
    if (editableNewPrice.value !== orderDetails.value?.newPrice) {
      content.price = editableNewPrice.value
    }
    if (editableNewAmount.value !== orderDetails.value?.newAmount) {
      content.amount = editableNewAmount.value
    }
  }
  
  store.confirmWithContent(content)
}

// Operation-specific styling class
const operationClass = computed(() => {
  switch (operationType.value) {
    case 'delete': return 'delete'
    case 'modify': return 'modify'
    default: return isBuyOrder.value ? 'buy' : 'sell'
  }
})

// Header configuration
const headerIcon = computed(() => {
  switch (operationType.value) {
    case 'delete': return Trash2
    case 'modify': return Edit3
    default: return isBuyOrder.value ? ArrowUpRight : ArrowDownRight
  }
})

const modalTitle = computed(() => {
  switch (operationType.value) {
    case 'delete': return 'Emir İptali'
    case 'modify': return 'Emir Değişikliği'
    default: return 'Emir Onayı'
  }
})

const modalSubtitle = computed(() => {
  switch (operationType.value) {
    case 'delete': return 'Emrinizi iptal etmek üzeresiniz'
    case 'modify': return 'Emrinizi değiştirmek üzeresiniz'
    default: return 'İşleminizi onaylayın'
  }
})

const confirmIcon = computed(() => {
  switch (operationType.value) {
    case 'delete': return Trash2
    case 'modify': return Edit3
    default: return CheckCircle
  }
})

const confirmButtonText = computed(() => {
  switch (operationType.value) {
    case 'delete': return 'Emri İptal Et'
    case 'modify': return 'Değişikliği Onayla'
    default: return 'Onayla'
  }
})

// Submit order specific
const isBuyOrder = computed(() => {
  const direction = orderDetails.value?.direction?.toLowerCase() || ''
  return direction.includes('alım') || direction.includes('buy') || direction.includes('al')
})

const orderDirection = computed(() => isBuyOrder.value ? 'buy' : 'sell')

const calculatedTotal = computed(() => {
  return editableAmount.value * editablePrice.value
})

function formatNumber(val: number | undefined): string {
  if (val == null || isNaN(val)) return '-'
  return Number(val).toLocaleString('tr-TR')
}

function formatCurrency(val: number | undefined): string {
  if (val == null || isNaN(val)) return '₺0,00'
  return '₺' + Number(val).toLocaleString('tr-TR', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })
}
</script>

<style scoped>
.confirmation-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
}

.confirmation-modal {
  width: 100%;
  max-width: 440px;
  margin: 16px;
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 20px;
  box-shadow: 
    0 25px 80px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  flex-shrink: 0;
}

.header-icon.buy {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.header-icon.sell {
  background: rgba(244, 63, 94, 0.15);
  color: #f43f5e;
  border: 1px solid rgba(244, 63, 94, 0.2);
}

.header-icon.modify {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.2);
}

.header-icon.delete {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.header-text {
  flex: 1;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.modal-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #94a3b8;
}

/* Countdown Timer */
.countdown-timer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  color: #818cf8;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.countdown-timer.urgent {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.countdown-value {
  min-width: 36px;
  text-align: center;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(244, 63, 94, 0.1);
  border-color: rgba(244, 63, 94, 0.3);
  color: #f43f5e;
}

/* Content */
.modal-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Order Card - Submit */
.order-card {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 16px;
  overflow: hidden;
}

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: rgba(15, 23, 42, 0.5);
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
}

.stock-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stock-symbol {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}

.stock-name {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
}

.order-direction-badge {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  letter-spacing: 0.05em;
}

.order-direction-badge.buy {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.25);
}

.order-direction-badge.sell {
  background: rgba(244, 63, 94, 0.15);
  color: #f43f5e;
  border: 1px solid rgba(244, 63, 94, 0.25);
}

.order-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: rgba(148, 163, 184, 0.08);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  background: rgba(15, 23, 42, 0.4);
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-label svg {
  color: #818cf8;
}

.detail-value {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}

.detail-value small {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}

/* Editable Fields */
.editable-field {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  transition: all 0.2s ease;
}

.editable-field:focus-within {
  border-color: rgba(99, 102, 241, 0.6);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.editable-field.compact {
  padding: 6px 10px;
}

.edit-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  width: 100%;
  min-width: 0;
}

.edit-input::-webkit-inner-spin-button,
.edit-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.edit-input[type=number] {
  -moz-appearance: textfield;
}

.field-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.estimated-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(99, 102, 241, 0.1);
  border-top: 1px solid rgba(99, 102, 241, 0.15);
}

.total-label {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

.total-value {
  font-size: 20px;
  font-weight: 800;
  color: #818cf8;
  letter-spacing: -0.02em;
}

/* Order Card - Modify */
.modify-card .modify-header {
  border-bottom: 1px solid rgba(251, 191, 36, 0.15);
}

.ref-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ref-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ref-value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  font-family: monospace;
}

.modify-type-badge {
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.25);
  border-radius: 8px;
}

.modify-changes {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.change-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.change-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 10px;
}

.change-item.old {
  border: 1px solid rgba(148, 163, 184, 0.1);
}

.change-item.new {
  border: 1px solid rgba(251, 191, 36, 0.25);
  background: rgba(251, 191, 36, 0.05);
}

.change-item.editable {
  padding: 10px 14px;
}

.change-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
}

.change-value {
  font-size: 16px;
  font-weight: 700;
  color: #94a3b8;
}

.change-value.highlight {
  color: #fbbf24;
}

.change-value small {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.change-arrow {
  color: #64748b;
  flex-shrink: 0;
}

.order-meta {
  padding: 12px 20px;
  font-size: 12px;
  color: #64748b;
  background: rgba(15, 23, 42, 0.3);
  border-top: 1px solid rgba(148, 163, 184, 0.08);
}

.meta-divider {
  margin: 0 8px;
}

/* Order Card - Delete */
.delete-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  text-align: center;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.delete-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 20px;
  color: #ef4444;
  margin-bottom: 20px;
}

.delete-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delete-label {
  font-size: 14px;
  color: #94a3b8;
}

.delete-ref {
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  font-family: monospace;
}

/* Warning Box */
.warning-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.15);
  border-radius: 12px;
  font-size: 13px;
  color: #fbbf24;
  line-height: 1.5;
}

.warning-box svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.warning-box.warning-modify {
  background: rgba(251, 191, 36, 0.08);
  border-color: rgba(251, 191, 36, 0.15);
}

.warning-box.warning-delete {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

/* Footer */
.modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  background: rgba(15, 23, 42, 0.5);
  border-top: 1px solid rgba(148, 163, 184, 0.1);
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.decline-btn {
  background: rgba(244, 63, 94, 0.1);
  border: 1px solid rgba(244, 63, 94, 0.2);
  color: #f43f5e;
}

.decline-btn:hover:not(:disabled) {
  background: rgba(244, 63, 94, 0.2);
  border-color: rgba(244, 63, 94, 0.3);
}

.confirm-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  color: #fff;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.25);
}

.confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.35);
  transform: translateY(-1px);
}

.confirm-btn.modify {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: rgba(245, 158, 11, 0.3);
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.25);
}

.confirm-btn.modify:hover:not(:disabled) {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.35);
}

.confirm-btn.delete {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-color: rgba(239, 68, 68, 0.3);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.25);
}

.confirm-btn.delete:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.35);
}

.confirm-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Animations */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .confirmation-modal,
.modal-fade-leave-active .confirmation-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .confirmation-modal,
.modal-fade-leave-to .confirmation-modal {
  transform: scale(0.95) translateY(10px);
  opacity: 0;
}

/* Responsive */
@media (max-width: 480px) {
  .confirmation-modal {
    max-width: 100%;
    margin: 8px;
    border-radius: 16px;
  }

  .modal-header {
    padding: 20px;
    flex-wrap: wrap;
  }

  .countdown-timer {
    order: 5;
    margin-top: 12px;
    width: 100%;
    justify-content: center;
  }

  .modal-content {
    padding: 20px;
  }

  .stock-symbol {
    font-size: 18px;
  }

  .edit-input {
    font-size: 14px;
  }

  .modal-footer {
    padding: 16px 20px;
  }

  .action-btn {
    padding: 12px 16px;
    font-size: 13px;
  }

  .change-row {
    flex-direction: column;
    gap: 8px;
  }

  .change-arrow {
    transform: rotate(90deg);
  }

  .change-item {
    width: 100%;
  }
}
</style>
